/**
 * Proposals and contracts, landing page.
 *
 * One component, one `kind` prop: a proposal is the offer, a contract is what
 * a signed proposal converts into — see the file-level comment on
 * `packages/db/src/schema/contracts.ts`. Sorting is by most recent, since the
 * thing you want is nearly always the one you were last working on.
 */
import { useState } from "react";
import { AssistantHint } from "../AssistantHint";
import { api, fmtAmount, unwrap } from "../api";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  SortTh,
  Table,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useResource } from "../useResource";
import { ClientPicker } from "./shared";

export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  issued: "Issued",
  accepted: "Accepted",
  approved: "Approved",
  declined: "Declined",
  expired: "Expired",
  void: "Void",
};

/** Approved is the one worth spotting from across the page — it means projects
 *  exist. Everything else reads as ordinary progress. */
export const CONTRACT_STATUS_STYLES: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-600",
  issued: "bg-blue-50 text-blue-700",
  accepted: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  declined: "bg-red-50 text-red-700",
  expired: "bg-neutral-100 text-neutral-500",
  void: "bg-neutral-100 text-neutral-400",
};

export function StatusPill({ status }: { status: string }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
        CONTRACT_STATUS_STYLES[status] ?? "bg-neutral-100 text-neutral-600"
      }`}
    >
      {CONTRACT_STATUS_LABELS[status] ?? status}
    </span>
  );
}

const KIND_COPY = {
  proposal: {
    panelTitle: "Proposals",
    newLabel: "New proposal",
    emptyTitle: "No proposals yet",
    emptyBody: "Start one here, or build one from a priced estimate on the Estimating page.",
    blurb:
      "A proposal is the document a client sees. Once it's accepted, convert it to a " +
      "contract to sign — that's what turns each part of it into a project.",
  },
  contract: {
    panelTitle: "Contracts",
    newLabel: "New contract",
    emptyTitle: "No contracts yet",
    emptyBody: "A contract is created by converting an accepted proposal — open one to convert it.",
    blurb:
      "A contract is the binding document a client signs. Approving one turns each part of " +
      "it into a project, with its payment schedule and its contractors already attached.",
  },
} as const;

export function Contracts({
  slug,
  kind,
  currency,
  role,
  onOpenContract,
  onOpenEstimate,
}: {
  slug: string;
  kind: "proposal" | "contract";
  currency: string;
  role: string;
  onOpenContract: (id: string) => void;
  onOpenEstimate?: (id: string) => void;
}) {
  const copy = KIND_COPY[kind];
  // Only a proposal is created directly; a contract only ever comes from
  // converting one, on the proposal's own detail page.
  const canCreate =
    kind === "proposal" && (role === "manager" || role === "admin" || role === "owner");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [clientId, setClientId] = useState("");

  const r = useResource({
    key: ["contracts", slug, kind],
    list: async () =>
      unwrap(await api.api.c[":slug"].contracts.$get({ param: { slug }, query: { kind } })),
    create: async (body: { title: string; clientId: string; currency: string }) =>
      unwrap(await api.api.c[":slug"].contracts.$post({ param: { slug }, json: body })),
  });

  const rows = r.query.data?.contracts ?? [];
  const table = useTable(rows, {
    sorters: {
      number: (x) => x.number,
      client: (x) => x.clientName,
      status: (x) => x.status,
      total: (x) => Number(x.total),
      created: (x) => x.createdAt,
    },
    initialSort: "created",
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await r.create({ title: title.trim(), clientId, currency });
      setTitle("");
      setClientId("");
      setOpen(false);
      onOpenContract((created as { contract: { id: string } }).contract.id);
    } catch {
      /* surfaced through r.formError */
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Only on proposals. A contract is never written from scratch — it comes
          from converting one — so offering to dictate it here would be
          advertising something the assistant is right to refuse. */}
      {kind === "proposal" && <AssistantHint what="Draft a proposal by describing the job." />}
      <Panel
        title={copy.panelTitle}
        action={
          canCreate && (
            <Button
              onClick={() => {
                setOpen(!open);
                r.clearErrors();
              }}
            >
              {open ? "Cancel" : copy.newLabel}
            </Button>
          )
        }
      >
        {open && (
          <form
            onSubmit={submit}
            className="mb-6 flex items-end gap-3 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
          >
            {r.formError && <ErrorNote>{r.formError}</ErrorNote>}
            <div className="flex-1">
              <Field label="Title" error={r.fieldErrors.title}>
                <input
                  className={input}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Franco kitchen & closet"
                  required
                />
              </Field>
            </div>
            <div className="flex-1">
              <ClientPicker
                slug={slug}
                value={clientId}
                onChange={setClientId}
                required
                error={r.fieldErrors.clientId}
              />
            </div>
            <Button type="submit" disabled={r.saving}>
              {r.saving ? "Creating…" : "Create"}
            </Button>
          </form>
        )}

        <p className="mb-4 text-sm text-neutral-500">{copy.blurb}</p>

        {r.query.isPending ? (
          <Loading />
        ) : r.query.isError ? (
          <ErrorNote>{(r.query.error as Error).message}</ErrorNote>
        ) : rows.length === 0 ? (
          <Empty title={copy.emptyTitle}>{copy.emptyBody}</Empty>
        ) : (
          <Table
            head={
              <>
                <SortTh sortKey="number" table={table}>
                  No.
                </SortTh>
                <Th>Title</Th>
                <SortTh sortKey="client" table={table}>
                  Client
                </SortTh>
                <SortTh sortKey="status" table={table}>
                  Status
                </SortTh>
                <Th right>Parts</Th>
                <SortTh sortKey="total" table={table} right>
                  Total
                </SortTh>
                <Th />
              </>
            }
          >
            {table.rows.map((row) => (
              <tr
                key={row.id}
                tabIndex={0}
                className="cursor-pointer border-b border-neutral-100 transition-colors even:bg-neutral-50 hover:bg-neutral-100 focus:bg-neutral-100 focus:outline-none"
                onClick={() => onOpenContract(row.id)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") onOpenContract(row.id);
                }}
              >
                <Td mono>{row.number}</Td>
                <Td>
                  {row.title}
                  {row.estimateId && kind === "proposal" && (
                    <div className="mt-0.5 text-xs font-normal text-neutral-400">
                      {onOpenEstimate ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenEstimate(row.estimateId!);
                          }}
                          className="underline underline-offset-2 hover:text-neutral-600"
                        >
                          From estimate {row.estimateName}
                        </button>
                      ) : (
                        <>From estimate {row.estimateName}</>
                      )}
                    </div>
                  )}
                  {kind === "proposal" && row.convertedToNumber && (
                    <div className="mt-0.5 text-xs font-normal text-neutral-400">
                      Converted to {row.convertedToNumber}
                    </div>
                  )}
                </Td>
                <Td muted>{row.clientName}</Td>
                <Td>
                  <StatusPill status={row.status} />
                </Td>
                <Td right muted>
                  {row.segments}
                </Td>
                <Td right mono>
                  {fmtAmount(row.total, row.currency)}
                </Td>
                <Td right>
                  <Button variant="ghost" onClick={() => onOpenContract(row.id)}>
                    Open
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
